import React, { useEffect } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Segment, Grid, Header, Icon, Form, Button } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import useWindowSize from "../../utils/useWindowSize";
import Mappa from "../common/Mappa";
import InfoWeb from "../common/InfoWeb";
import PrezzoOfferta from "./PrezzoOfferta";
import useOffertaBooking from "./useOffertaBooking";

/**
 * Interfaccia di prenotazione di una "Offerta / Pacchetto" (corso con flag CorsoPrivato).
 *
 * Differenze rispetto a un corso collettivo:
 *  - la durata è fissa: sono le giornate della sessione, nessun selettore;
 *  - il giorno di inizio è fisso: se il corso ha più finestre di disponibilità si sceglie il
 *    periodo, altrimenti è in sola lettura. Nessun calendario;
 *  - il listino è indicizzato per numero di persone anziché per giornate (`numGiorni` ==
 *    numero di persone) e il prezzo è già il totale della comitiva.
 */
function DettaglioOfferta({ corso, sessioniCorso, datiScuola, promozioniNew, appData, ...props }) {
  const {
    sessioni,
    idxSessione,
    setIdxSessione,
    sessioneSelezionata,
    giorniOfferta,
    numAllievi,
    setNumAllievi,
    numAllieviDisponibili,
    periodoOptions,
    listinoInUso,
    coordinateRitrovo,
    mapVisible,
    setMapVisible,
    buildAvantiArgs,
  } = useOffertaBooking({ corso, sessioniCorso, datiScuola, promozioniNew, appData });

  const windowSize = useWindowSize();
  const { t, i18n } = useTranslation([datiScuola.customTranslationNamespace]);

  useEffect(() => {
    i18n.emit("languageChanged");
  }, [datiScuola.customTranslationNamespace]);

  if (!sessioneSelezionata) {
    return (
      <Segment basic textAlign="center">
        {t("nessuna_disponibilita")}
      </Segment>
    );
  }

  return (
    <Segment
      basic
      className="padding_0_lr"
      style={{ marginTop: "0px", paddingTop: "0px", paddingLeft: "0px" }}
    >
      <Grid columns="equal" stackable textAlign="left">
        <Grid.Column
          style={{
            paddingLeft: windowSize.width > 600 ? "0px" : "14px",
            margin: "0px",
            paddingTop: "0px",
          }}
          mobile="16"
          largeScreen="10"
        >
          <Segment>
            <Header as="h3">
              <Icon name="calendar alternate outline"></Icon>
              <Header.Content>{t("programma")}</Header.Content>
            </Header>
            <ul>
              {sessioneSelezionata.righe?.map((riga, index) => (
                <li key={`${riga.rigaStampa}-${index}`}>{riga.rigaStampa}</li>
              ))}
            </ul>
          </Segment>

          <div
            style={{
              marginLeft: windowSize.width > 600 ? "2em" : "2px",
              marginTop: "2em",
            }}
          >
            <InfoWeb
              testi={datiScuola.infoWeb.filter((x) => x.sezione === 0)}
              sezione={0}
            />
          </div>
        </Grid.Column>

        <Grid.Column mobile="16" largeScreen="6">
          <Segment>
            <Header as="h3">
              <Icon name="calendar alternate outline"></Icon>
              <Header.Content>{t("dettagli_corso")}</Header.Content>
            </Header>

            <Form>
              {sessioni.length > 1 ? (
                <Form.Dropdown
                  selection
                  label={t("periodo")}
                  options={periodoOptions}
                  value={idxSessione}
                  onChange={(e, data) => setIdxSessione(data.value)}
                />
              ) : (
                <Form.Input
                  label={t("periodo")}
                  value={`${moment(sessioneSelezionata.dal).format(
                    "DD/MM/YYYY"
                  )} → ${moment(sessioneSelezionata.al).format("DD/MM/YYYY")}`}
                  readOnly
                  fluid
                  onChange={() => {}}
                />
              )}

              <Form.Input
                label={t("durata_pacchetto")}
                value={t("numero_giornate", { count: giorniOfferta })}
                readOnly
                fluid
                onChange={() => {}}
              />

              <Form.Dropdown
                selection
                label={t("numero_persone")}
                options={numAllieviDisponibili}
                value={numAllievi}
                onChange={(e, data) => setNumAllievi(data.value)}
              />

              <div style={{ marginTop: "1em" }}>
                <label>
                  <strong>{t("ritrovo")}</strong>
                </label>
              </div>
              <div>
                <span>{sessioneSelezionata.ritrovo}</span>
                {coordinateRitrovo && (
                  <span style={{ marginLeft: "0.5em" }}>
                    <Icon
                      name="map pin"
                      circular
                      link
                      color="red"
                      onClick={() => setMapVisible(!mapVisible)}
                    ></Icon>
                  </span>
                )}
              </div>
              {mapVisible && coordinateRitrovo && (
                <Mappa
                  ritrovo={t("ritrovo")}
                  lat={coordinateRitrovo.lat}
                  lng={coordinateRitrovo.lng}
                  zoom={coordinateRitrovo.z}
                ></Mappa>
              )}
            </Form>

            <PrezzoOfferta
              listinoInUso={listinoInUso}
              numAllievi={numAllievi}
              datiScuola={datiScuola}
            />
          </Segment>

          <Segment basic textAlign="center">
            <Button
              disabled={!listinoInUso}
              primary
              size="big"
              onClick={() => {
                props.onAvantiClick(...buildAvantiArgs());
              }}
            >
              <Icon name="cart" />
              {t("avanti")}
            </Button>
          </Segment>
        </Grid.Column>
      </Grid>
    </Segment>
  );
}

DettaglioOfferta.propTypes = {
  corso: PropTypes.object.isRequired,
  sessioniCorso: PropTypes.object.isRequired,
  datiScuola: PropTypes.object.isRequired,
  onAvantiClick: PropTypes.func.isRequired,
};

export default DettaglioOfferta;
